import { useEffect, useMemo, useRef } from "react";
import {
  MapContainer, TileLayer, Marker, Circle, Popup, Polyline, LayersControl, useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { formatTime } from "@/lib/timezone";

// Fix default marker icon paths for CRA builds
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Colored circle "pin" icons keyed by point type + geofence status.
// `n` is the chronological sequence number (1-based) shown inside the pin so
// admins can see "this was the 1st ping, 2nd, 3rd…" without hovering.
function coloredIcon(color, n) {
  return L.divIcon({
    className: "",
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    html: `
      <div style="
        width:28px;height:28px;border-radius:50%;
        background:${color};border:2px solid #0c1524;
        box-shadow:0 0 0 2px ${color}55;
        display:flex;align-items:center;justify-content:center;
        color:#0c1524;font-family:'JetBrains Mono',monospace;
        font-size:12px;font-weight:700;line-height:1;
      ">${n}</div>`,
  });
}

const TYPE_COLOR = {
  punch_in: "#34d399",           // emerald — punch-in
  punch_out: "#f472b6",          // pink — punch-out
  heartbeat_ok: "#60a5fa",       // blue — heartbeat inside
  heartbeat_bad: "#fb923c",      // orange — heartbeat outside
  punch_in_rejected: "#ef4444",  // red — rejected punch-in
  punch_out_rejected: "#ef4444", // red — rejected punch-out
};

function colorFor(p) {
  if (p.type === "punch_in") return TYPE_COLOR.punch_in;
  if (p.type === "punch_out") return TYPE_COLOR.punch_out;
  if (p.type === "punch_in_rejected") return TYPE_COLOR.punch_in_rejected;
  if (p.type === "punch_out_rejected") return TYPE_COLOR.punch_out_rejected;
  return p.inside_geofence ? TYPE_COLOR.heartbeat_ok : TYPE_COLOR.heartbeat_bad;
}

function FitBounds({ bounds, fitKey }) {
  const map = useMap();
  const lastKeyRef = useRef(null);
  useEffect(() => {
    // Only refit when the underlying dataset changes (fitKey moves), not on
    // every visibility toggle. This preserves the admin's pan/zoom when they
    // check or uncheck timeline rows.
    if (fitKey && fitKey === lastKeyRef.current) return;
    if (bounds && bounds.length > 0) {
      const b = L.latLngBounds(bounds);
      if (b.isValid()) map.fitBounds(b, { padding: [40, 40], maxZoom: 18 });
    }
    lastKeyRef.current = fitKey;
    // eslint-disable-next-line
  }, [fitKey]);
  return null;
}

export default function TrackingMap({ points = [], centers = [], tz = null, fitKey = null, className = "" }) {
  const positions = useMemo(
    () => points
      .filter((p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude))
      .map((p) => [p.latitude, p.longitude]),
    [points]
  );
  const centerPositions = useMemo(
    () => centers
      .filter((c) => Number.isFinite(c.latitude) && Number.isFinite(c.longitude))
      .map((c) => [c.latitude, c.longitude]),
    [centers]
  );

  const allBounds = useMemo(() => [...positions, ...centerPositions], [positions, centerPositions]);
  const fallback = [12.9352, 77.6245]; // Bengaluru — sensible default

  const mapRef = useRef(null);
  return (
    <div
      data-testid="tracking-map"
      className={`w-full border border-border overflow-hidden ${className || "h-[560px]"}`}
    >
      <MapContainer
        ref={mapRef}
        center={allBounds[0] || fallback}
        zoom={allBounds.length ? 15 : 4}
        scrollWheelZoom
        style={{ height: "100%", width: "100%", background: "hsl(215 60% 8%)" }}
      >
        <LayersControl position="topright">
          {/* All tile providers below are free and require no API key */}
          <LayersControl.BaseLayer name="Map">
            <TileLayer
              attribution='&copy; OpenStreetMap contributors &copy; CARTO'
              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Streets (dark)">
            <TileLayer
              attribution='&copy; OpenStreetMap contributors &copy; CARTO'
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Terrain">
            <TileLayer
              attribution='&copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)'
              url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
              maxZoom={17}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Satellite">
            <TileLayer
              attribution="&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer checked name="Hybrid (sat + labels)">
            <TileLayer
              attribution="&copy; Esri &amp; contributors"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        {/* Geofence circles for each of the user's centers */}
        {centers.map((c) => (
          Number.isFinite(c.latitude) && Number.isFinite(c.longitude) ? (
            <Circle
              key={c.id}
              center={[c.latitude, c.longitude]}
              radius={Math.max(c.radius_meters || 100, 20)}
              pathOptions={{ color: "#fbbf24", fillColor: "#fbbf24", fillOpacity: 0.1, weight: 2 }}
            >
              <Popup>
                <b>{c.name}</b><br/>Geofence radius: {c.radius_meters} m
              </Popup>
            </Circle>
          ) : null
        ))}

        {/* Path connecting consecutive location points */}
        {positions.length > 1 && (
          <Polyline
            positions={positions}
            pathOptions={{ color: "#60a5fa", weight: 2, opacity: 0.6, dashArray: "6 6" }}
          />
        )}

        {/* Pins */}
        {points.map((p, i) => {
          if (!Number.isFinite(p.latitude) || !Number.isFinite(p.longitude)) return null;
          const seq = (p._seq != null) ? p._seq : (i + 1);
          const icon = coloredIcon(colorFor(p), seq);
          const t = p.timestamp ? formatTime(p.timestamp, tz) : "—";
          return (
            <Marker
              key={i}
              position={[p.latitude, p.longitude]}
              icon={icon}
              data-testid={`pin-${p.type}-${i}`}
            >
              <Popup>
                <div style={{ fontFamily: "system-ui, sans-serif", fontSize: 13 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    #{seq} · {p.type === "punch_in" && "Punch-in"}
                    {p.type === "punch_out" && "Punch-out"}
                    {p.type === "punch_in_rejected" && "Punch-in REJECTED · outside geofence"}
                    {p.type === "punch_out_rejected" && "Punch-out REJECTED · outside geofence"}
                    {p.type === "heartbeat" && (p.inside_geofence ? "Heartbeat · inside geofence" : "Heartbeat · OUTSIDE (breach)")}
                  </div>
                  <div>Time: <b>{t}</b></div>
                  <div>Lat/Lng: {p.latitude.toFixed(5)}, {p.longitude.toFixed(5)}</div>
                  {p.distance_meters != null && (
                    <div>Distance from center: <b>{p.distance_meters} m</b></div>
                  )}
                  {p.center_name && <div>Center: {p.center_name}</div>}
                </div>
              </Popup>
            </Marker>
          );
        })}

        <FitBounds bounds={allBounds} fitKey={fitKey} />
      </MapContainer>
    </div>
  );
}
